import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchPerformanceCenter, markIncentivePaid, markAdminAlertReviewed, verifyReview, rejectReview } from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { Card, CardHeader } from '../components/ui/Card';
import { MetricCard } from '../components/ui/MetricCard';
import { PageHeader } from '../components/ui/PageHeader';

function money(n: number) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

// Management performance / Field Lead incentives dashboard
// (docs/V6_ARCHITECTURE_AND_MIGRATION_PLAN.md §8, Stage 6). Reuses
// appGetPerformanceCenter() as-is — its role-based scoping (management
// sees the team leaderboard, a Field Lead sees only their own incentives,
// an Estimator sees only referrals) is unchanged. Owner/Admin also get
// mark-incentive-paid and admin-alert-review actions here — unmodified
// appMarkIncentivePaid/appMarkAdminAlertReviewed.
export function PerformancePage() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['performance'], queryFn: fetchPerformanceCenter });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['performance'] });
  }

  const markPaidMutation = useMutation({
    mutationFn: (incentiveId: string) => markIncentivePaid(incentiveId),
    onSuccess: (result) => result.ok && invalidate(),
  });

  const markReviewedMutation = useMutation({
    mutationFn: (alertId: string) => markAdminAlertReviewed(alertId, 'Reviewed'),
    onSuccess: (result) => result.ok && invalidate(),
  });

  const verifyReviewMutation = useMutation({
    mutationFn: (reviewId: string) => verifyReview(reviewId),
    onSuccess: (result) => result.ok && invalidate(),
  });

  const rejectReviewMutation = useMutation({
    mutationFn: (reviewId: string) => rejectReview(reviewId),
    onSuccess: (result) => result.ok && invalidate(),
  });

  if (query.isLoading) return <LoadingState label="Loading performance…" />;
  if (query.isError) {
    return <ErrorState message={query.error instanceof Error ? query.error.message : 'Could not load performance data.'} onRetry={() => query.refetch()} />;
  }
  if (!query.data?.ok) {
    return <ErrorState message={query.data?.message || 'Could not load performance data.'} onRetry={() => query.refetch()} />;
  }

  const { summary, employees = [], myIncentives = [], allIncentives = [], alerts = [], pendingReviews = [], scope } = query.data;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Team" title="Performance" />

      {summary && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <MetricCard label="Upsells" value={summary.upsellsText} money />
          <MetricCard label="Discounts" value={summary.discountsText} money />
          <MetricCard label="Referrals" value={summary.referrals} />
          <MetricCard label="Reviews" value={summary.reviews} />
        </div>
      )}

      {scope === 'SELF' && (
        <Card>
          <CardHeader title="My Incentives" />
          {myIncentives.length === 0 ? (
            <EmptyState>No incentives recorded yet.</EmptyState>
          ) : (
            <ul className="space-y-2">
              {myIncentives.map((inc, i) => (
                <li key={i} className="flex items-center justify-between gap-3 rounded-lg border border-ink-border bg-ink p-3 text-sm">
                  <span className="text-slate-300">{String(inc.source ?? inc.type ?? '—')}</span>
                  <span className="font-semibold text-gold-200">{money(Number(inc.amount ?? 0))}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {employees.length > 0 && (
        <Card>
          <CardHeader title="Team Leaderboard" />
          <ul className="space-y-2">
            {employees.map((emp) => (
              <li key={emp.username} className="flex items-center justify-between gap-3 rounded-lg border border-ink-border bg-ink p-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-white">{emp.name}</p>
                  <p className="truncate text-sm text-slate-400">
                    {emp.referrals} referral{emp.referrals === 1 ? '' : 's'} · {emp.reviews} review{emp.reviews === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-semibold text-gold-200">{emp.upsellsText}</p>
                  <p className="text-xs text-slate-500">bonus {emp.bonusEarnedText}</p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {allIncentives.filter((inc) => inc.status === 'Earned').length > 0 && (
        <Card>
          <CardHeader title="Earned Incentives Awaiting Payout" />
          <ul className="space-y-2">
            {allIncentives
              .filter((inc) => inc.status === 'Earned')
              .map((inc) => (
                <li key={inc.incentiveId} className="flex items-center justify-between gap-3 rounded-lg border border-ink-border bg-ink p-3 text-sm">
                  <span className="text-slate-300">
                    {inc.employeeName} — {inc.type}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gold-200">{money(inc.amount)}</span>
                    <Button variant="secondary" busy={markPaidMutation.isPending} busyLabel="Marking…" onClick={() => markPaidMutation.mutate(inc.incentiveId)}>
                      Mark Paid
                    </Button>
                  </div>
                </li>
              ))}
          </ul>
          {markPaidMutation.data && !markPaidMutation.data.ok && <ErrorState message={markPaidMutation.data.message || 'Could not mark this incentive paid.'} />}
        </Card>
      )}

      {pendingReviews.length > 0 && (
        <Card>
          <CardHeader title="Pending Review Verifications" />
          <ul className="space-y-2">
            {pendingReviews.map((rev) => (
              <li key={rev.reviewId} className="rounded-lg border border-ink-border bg-ink p-3 text-sm">
                <p className="font-semibold text-white">
                  {rev.fieldLead} — {rev.platform}
                </p>
                {rev.reviewUrl && (
                  <a href={rev.reviewUrl} target="_blank" rel="noreferrer" className="mt-1 block break-all text-gold-300 hover:text-gold-200">
                    {rev.reviewUrl}
                  </a>
                )}
                <div className="mt-2 flex gap-2">
                  <Button variant="secondary" busy={verifyReviewMutation.isPending} busyLabel="Verifying…" onClick={() => verifyReviewMutation.mutate(rev.reviewId)}>
                    Verify
                  </Button>
                  <Button variant="secondary" busy={rejectReviewMutation.isPending} busyLabel="Rejecting…" onClick={() => rejectReviewMutation.mutate(rev.reviewId)}>
                    Reject
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          {verifyReviewMutation.data && !verifyReviewMutation.data.ok && <ErrorState message={verifyReviewMutation.data.message || 'Could not verify this review.'} />}
          {rejectReviewMutation.data && !rejectReviewMutation.data.ok && <ErrorState message={rejectReviewMutation.data.message || 'Could not reject this review.'} />}
        </Card>
      )}

      {alerts.length > 0 && (
        <Card>
          <CardHeader title="Admin Alerts" />
          <ul className="space-y-2">
            {alerts.map((alert) => (
              <li key={alert.alertId} className="space-y-1.5 rounded-lg border border-ink-border bg-ink p-3 text-sm">
                <p className="font-semibold text-white">{alert.title}</p>
                <p className="text-slate-400">{alert.detail}</p>
                <Button variant="secondary" busy={markReviewedMutation.isPending} busyLabel="Marking…" onClick={() => markReviewedMutation.mutate(alert.alertId)}>
                  Mark Reviewed
                </Button>
              </li>
            ))}
          </ul>
          {markReviewedMutation.data && !markReviewedMutation.data.ok && <ErrorState message={markReviewedMutation.data.message || 'Could not mark this alert reviewed.'} />}
        </Card>
      )}
    </div>
  );
}
